import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import { DirectUpload } from "@rails/activestorage"

// MED-43 — how long the page waits for the reading before it takes the medium
// as it stands, and how often it asks in between. A blocked page with no end
// is worse than a placeholder that shows its symbol for a moment.
const READING_PATIENCE = 60_000
const READING_INTERVAL = 1_000

// MED-39 — picking a medium for one placeholder. The picker itself is a single
// modal shared by the whole form (six placeholders must not mean six copies of
// the library), so this controller does three things: it points the modal's
// Turbo frame at what fits *its* slot, it listens for a tile being tapped while
// the modal is open, and it writes the result back into its own field.
//
// The listener is delegated onto the modal and removed again when the modal
// closes. That is what keeps two placeholders on the same page from both
// answering the same tap.
export default class extends Controller {
  static targets = ["field", "image", "placeholder", "name", "clear", "error"]
  static values = {
    url: String, adoptUrl: String, kind: String, minWidth: Number, ratio: String
  }

  open() {
    const modal = document.getElementById("media_picker_modal")
    if (!modal) return

    modal.querySelector("turbo-frame#media_picker").src = this.pickerUrl()

    this.listener = (event) => this.choose(event)
    modal.addEventListener("click", this.listener)
    modal.addEventListener("hidden.bs.modal", () => this.release(modal), { once: true })

    Modal.getOrCreateInstance(modal).show()
  }

  // MED-13 rides along: the slot's minimum width and aspect ratio are what let
  // the tiles mark a medium that would only warn (VOR-47, VOR-82).
  // MAN-2 — the address of the picker carries the tenant, so it comes from the
  // server rather than being written out here: a path built in the browser
  // would be the one place that silently loses it.
  pickerUrl() {
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("kind", this.kindValue)
    if (this.hasMinWidthValue && this.minWidthValue > 0) {
      url.searchParams.set("min_width", this.minWidthValue)
    }
    if (this.hasRatioValue && this.ratioValue) {
      url.searchParams.set("ratio", this.ratioValue)
    }
    return url.toString()
  }

  choose(event) {
    const tile = event.target.closest("[data-media-id]")
    if (!tile) return

    this.take(tile)
    // The upload wins over a pick and the other way round — sending both would
    // leave the server guessing (MED-2).
    this.resetFileInput()
    this.announce()

    Modal.getOrCreateInstance(document.getElementById("media_picker_modal")).hide()
  }

  // MED-43 — a file chosen through "Browse" ends where a pick ends: uploaded
  // (MED-41), a medium of the library (MED-2), read (MED-9), and then in the
  // field and in the preview. The page is blocked until it is there, so
  // nobody leaves the step or fills the field twice in the meantime.
  async upload(event) {
    const input = event.target
    const file = input.files?.[0]
    if (!file) return

    this.showError("")
    this.block()
    try {
      this.status("storing")
      const signedId = await this.store(file, input.dataset.directUploadUrl)

      this.status("adopting")
      let piece = await this.adopt(signedId)

      // The reading runs in the background (MED-9); its end is asked for
      // rather than waited for on the stream, because on a fast machine it is
      // over before the answer above has even arrived.
      const deadline = Date.now() + READING_PATIENCE
      while (piece.dataset.mediaAnalysis === "pending" && Date.now() < deadline) {
        this.status("reading")
        await new Promise((resolve) => setTimeout(resolve, READING_INTERVAL))
        piece = await this.ask(piece.dataset.mediaUrl)
      }
      if (piece.dataset.mediaAnalysis === "unreadable") throw new Error(piece.dataset.mediaRefusal)

      this.take(piece)
      // The preview is handed the same address as the field, so a picture
      // the field has decoded is one the preview shows at once.
      await this.imageTarget.decode().catch(() => {})
      this.announce()
    } catch (error) {
      this.showError(error.message || this.blocker().dataset.failed)
    } finally {
      // The file is a medium now, or it was refused; either way it must not
      // travel with the form a second time (MED-2).
      this.resetFileInput()
      this.unblock()
    }
  }

  // MED-41 — straight to storage, and the bar says how far it got.
  store(file, url) {
    return new Promise((resolve, reject) => {
      const upload = new DirectUpload(file, url, {
        directUploadWillStoreFileWithXHR: (request) => {
          request.upload.addEventListener("progress", (event) => {
            if (event.lengthComputable) this.progress((event.loaded / event.total) * 100)
          })
        }
      })
      upload.create((error, blob) => (error ? reject(new Error()) : resolve(blob.signed_id)))
    })
  }

  adopt(signedId) {
    const body = new FormData()
    body.append("signed_id", signedId)
    body.append("kind", this.kindValue)

    return this.ask(this.adoptUrlValue, {
      method: "POST", body, headers: { "X-CSRF-Token": this.token() }
    })
  }

  // The answer is a piece of markup the server drew (media_assets/_placement):
  // a refusal is plain text worth showing, anything else is a failure.
  async ask(url, options = {}) {
    const response = await fetch(url, { credentials: "same-origin", ...options })
    const text = await response.text()
    if (response.status === 422) throw new Error(text)
    if (!response.ok) throw new Error()

    const template = document.createElement("template")
    template.innerHTML = text
    return template.content.firstElementChild
  }

  // What a tapped tile and an uploaded file have in common: an id, a name and
  // the medium's picture. Read out of the piece rather than off a data
  // attribute of its own: the picture arrives on its own when the file has
  // been read (MED-42), and a second copy of its address would then be the
  // stale one.
  take(piece) {
    const image = piece.querySelector("[data-media-picker-target=image]")
    const source = image && !image.classList.contains("d-none") ? image.getAttribute("src") : ""

    this.fieldTarget.value = piece.dataset.mediaId
    this.showTile(source)
    // MED-42 addresses the picture by its medium, so the field answers to the
    // one it now holds — a medium taken before it was read still gets its
    // picture. The parent is the box of media_assets/_picture, which carries
    // no attribute at all while the field is empty.
    this.imageTarget.parentElement.dataset.mediaPicture = piece.dataset.mediaId
    this.nameTarget.textContent = piece.dataset.mediaName
    this.clearTarget.classList.remove("d-none")
    this.showError("")
  }

  // MED-40 — out again in one step. A media placeholder may stay empty
  // (SPT-29), so this is a state the form has to be able to reach.
  clear() {
    this.fieldTarget.value = ""
    this.showTile("")
    this.nameTarget.textContent = this.nameTarget.dataset.emptyLabel || ""
    this.clearTarget.classList.add("d-none")
    this.resetFileInput()
    this.announce()
  }

  // Setting a hidden field fires nothing of its own, so whoever is listening for
  // a change — the autosave and the preview beside the field (ASS-26, ASS-37) —
  // has to be told.
  announce() {
    this.fieldTarget.dispatchEvent(new Event("change", {bubbles: true}))
  }

  // --- the blocked page (MED-43) ---------------------------------------------

  blocker() {
    return document.getElementById("media_upload_modal")
  }

  block() {
    this.progress(0)
    Modal.getOrCreateInstance(this.blocker()).show()
  }

  unblock() {
    Modal.getOrCreateInstance(this.blocker()).hide()
  }

  // Past the upload there is nothing left to count, so the bar stays full and
  // moves instead.
  status(name) {
    const waiting = name !== "storing"
    this.blocker().querySelector("[data-upload-state]").textContent = this.blocker().dataset[name]
    this.blocker().querySelector(".progress-bar")
      .classList.toggle("progress-bar-striped", waiting)
    this.blocker().querySelector(".progress-bar")
      .classList.toggle("progress-bar-animated", waiting)
    if (waiting) this.progress(100)
  }

  progress(percent) {
    const bar = this.blocker().querySelector(".progress-bar")
    bar.style.width = `${Math.round(percent)}%`
    bar.setAttribute("aria-valuenow", Math.round(percent))
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.toggle("d-none", !message)
  }

  token() {
    return document.querySelector("meta[name=csrf-token]")?.content || ""
  }

  showTile(source) {
    this.imageTarget.src = source || ""
    this.imageTarget.classList.toggle("d-none", !source)
    this.placeholderTarget.classList.toggle("d-none", Boolean(source))
  }

  resetFileInput() {
    const file = this.element.querySelector("input[type=file]")
    if (file) file.value = ""
  }

  release(modal) {
    modal.removeEventListener("click", this.listener)
  }
}
